import type { Registry } from '../registry';

type Theme = 'light' | 'dark' | 'system';

/**
 * Apply the default theme behavior — toggles `light`/`dark` classes on
 * `<html>` and persists the choice to `localStorage['theme-preference']`.
 * Ported verbatim from `hosted-sites/templates/starter/src/site-functions/setTheme.ts`
 * so that hosts that don't register a custom handler get identical behavior.
 */
function applyDefaultTheme(theme: Theme): { success: boolean; theme: string } {
  if (!['light', 'dark', 'system'].includes(theme)) {
    return { success: false, theme: 'unknown' };
  }

  const root = document.documentElement;

  if (theme === 'system') {
    root.classList.remove('light', 'dark');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.add(prefersDark ? 'dark' : 'light');
  } else {
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }

  try {
    localStorage.setItem('theme-preference', theme);
  } catch {
    // ignore (private mode / disabled storage)
  }

  return { success: true, theme };
}

/**
 * Theme built-in.
 *
 * Auto-injects a `setTheme` site-function matching the starter's setTheme.ts
 * shape. If the host registered a theme handler via `Mobeus.registerThemeHandler`,
 * that handler is invoked; otherwise the default DOM behavior runs.
 */
export function installThemeBuiltin(registry: Registry): void {
  registry.registerSystem('setTheme', {
    description: 'Switch the website theme between light, dark, or system preference',
    schema: {
      type: 'object',
      properties: { theme: { type: 'string', enum: ['light', 'dark', 'system'] } },
      required: ['theme'],
    },
    defaults: { theme: 'system' },
    fn: (args: { theme: Theme }) => {
      const theme = args?.theme ?? 'system';
      const handler = registry.getThemeHandler();
      if (handler) {
        try {
          handler(theme);
          return { success: true, theme };
        } catch (e) {
          return {
            success: false,
            error: `theme handler threw: ${String((e as Error)?.message ?? e)}`,
          };
        }
      }
      return applyDefaultTheme(theme);
    },
  });
}
